import React, { useCallback, useRef } from "react";

import cn from "classnames";

import { useDrop } from "react-dnd";

import { COLUMN_DND_TYPE, DIMENSION_COLUMN_DND_TYPE } from "./configUtils";

import { StateSetter } from "../../utils/types";

import SortItem from "./SortItem";

import { useSortAdder } from "../PerformanceGrid";

import { Preset, PresetChanges, SortInfo } from "@blisspointmedia/bpm-types/dist/Performance";

interface SortingBoxProps<P extends Preset> {
  presetChanges: PresetChanges<P>;
  setPresetChanges: StateSetter<PresetChanges<P>>;
  setHoverItem: StateSetter<string | undefined>;
  colorMap: Record<string, string | undefined>;
}

const SortingBox = <P extends Preset>({
  presetChanges,
  setPresetChanges,
  setHoverItem,
  colorMap,
}: SortingBoxProps<P>): JSX.Element => {
  const newSortInfo = presetChanges.defaultSorting || [];
  const setNewSortInfo = useCallback(
    (func: SortInfo[] | ((arr: SortInfo[]) => SortInfo[])) => {
      setPresetChanges(changes => ({
        ...changes,
        defaultSorting: typeof func === "function" ? func(changes.defaultSorting || []) : func,
      }));
    },
    [setPresetChanges]
  );

  const addSorting = useSortAdder(setNewSortInfo);

  const sortingBoxRef = useRef<HTMLDivElement>(null);

  const [{ isOver }, drop] = useDrop({
    accept: [COLUMN_DND_TYPE, DIMENSION_COLUMN_DND_TYPE],
    canDrop: item => !newSortInfo?.find(existing => existing.id === item.id),
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
    }),
    drop: (item: { type: typeof COLUMN_DND_TYPE; id: string }) => addSorting(item.id),
  });

  drop(sortingBoxRef);

  return (
    <div className={cn("sortingBox", { isOver })} ref={sortingBoxRef}>
      <div>Sort Order</div>
      {!newSortInfo?.length && <div className="dragHere">Drag and drop here</div>}
      <div className="sortItemBox">
        {newSortInfo?.map(item => (
          <SortItem
            key={item.id}
            item={item}
            setHoverItem={setHoverItem}
            colorMap={colorMap}
            onClick={() => addSorting(item.id)}
            setNewSortInfo={setNewSortInfo}
          />
        ))}
      </div>
    </div>
  );
};

export default SortingBox;
